'use client';

import InputRadio from '@/components/input/InputRadio/InputRadio';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import type { GfWorkDetail } from '@/features/gfWorks/types';
import { groupProductsByCategory } from '@/features/gfWorks/utils/groupProductsByCategory';
import type { Product } from '@/types';
import { formatCreatorNames } from '@/utils/formatters';
import { getImageLayoutSizes } from '@/utils/nextImage';
import { useMemo } from 'react';
import WorksDetailThumbnail from '../WorksDetailThumbnail/WorksDetailThumbnail';
import styles from './WorkDetailSidebar.module.scss';

function WorkDetailSidebar({
  work,
  products,
  selectedProductId,
  onItemFilterToggle,
  onRemoveItemFilter,
  isLoading,
}: {
  work: GfWorkDetail;
  products: Product[];
  selectedProductId: number | null;
  onItemFilterToggle: (productId: number) => void;
  onRemoveItemFilter: () => void;
  isLoading: boolean;
}) {
  // products が変わるたびにグループ再構築が走るのを防ぐためメモ化
  const groupedProducts = useMemo(() => groupProductsByCategory(products), [products]);

  return (
    <aside className={styles.sidebar}>
      {/* 作品画像 */}
      <div className={styles.workImageContainer}>
        <NextImageView
          src={work.imagePath}
          alt={work.name}
          fill
          sizes={getImageLayoutSizes({ sp: '24vw', pc: '200px' })}
          objectFit='contain'
        />
      </div>

      {/* 作品名 + クリエイター + コマデータ出典元 */}
      <div>
        <h2 className={styles.workName}>{work.name}</h2>
        <p className={styles.creatorName}>{formatCreatorNames(work.creators)}</p>
        <p className={styles.source}>
          <span className={styles.sourceLabel}>コマデータ出典元：</span>
          {work.source}
        </p>
      </div>

      <hr className={styles.divider} />

      {/* クリアボタン */}
      <button type='button' className={styles.clearButton} onClick={onRemoveItemFilter} disabled={isLoading}>
        クリア
      </button>

      {/* アイテムで絞り込む */}
      <div className={styles.itemFilterSection}>
        <h3 className={styles.itemFilterTitle}>アイテムで絞り込む</h3>
        <ul className={styles.itemFilterList}>
          <li className={styles.itemFilterListItem}>
            <InputRadio
              name='itemFilter'
              inputValue='all'
              checked={selectedProductId === null}
              onChange={() => onRemoveItemFilter()}
            >
              すべて
            </InputRadio>
          </li>
          {groupedProducts.map((group) => (
            <li key={group.categoryName} className={styles.itemFilterGroup}>
              <p className={styles.itemFilterGroupTitle}>{group.categoryName}</p>
              <ul className={styles.itemFilterSubList}>
                {group.products.map((product) => (
                  <li className={styles.itemFilterListItem} key={product.id}>
                    <InputRadio
                      name='itemFilter'
                      inputValue={String(product.id)}
                      checked={selectedProductId === product.id}
                      onChange={() => onItemFilterToggle(product.id)}
                    >
                      {product.name}
                    </InputRadio>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>

      <hr className={styles.divider} />

      <WorksDetailThumbnail work={work} />
    </aside>
  );
}

export default WorkDetailSidebar;
